import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
  Card,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Tooltip,
} from "@mui/material";
import MiniStatisticsCard from "components/Cards/StatisticsCards/MiniStatisticsCard";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { getUproColor } from "utils/colors";
import { makeNiceText } from "utils/formatters";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "60%", xxl: "50%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalNuevaTransferencia({
  open,
  handleClose,
  origen,
  refetch,
  selectedMovimiento,
}) {
  const [destino, setDestino] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isWriting, setIsWriting] = useState(false);
  const [search, setSearch] = useState("");
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const { user } = useContext(UserContext);
  const { handleSnackbar } = useContext(MessageManager);

  const [getSucursales, { loading: loadingSucursales }] = useLazyQuery(
    gql`
      query getSucursales($filter: JSON) {
        sucursales(filter: $filter) {
          id
          nombre
          administraStock
          principal
        }
      }
    `
  );

  const [getProductos, { data, loading }] = useLazyQuery(
    gql`
      query getProductos(
        $page: Int
        $order: Order
        $filter: JSON
        $idSucursal: ID
        $search: String
      ) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(
          pagination: { page: $page, limit: 50 }
          order: $order
          filter: $filter
          search: $search
        ) {
          id
          codigo
          tipo
          nombre
          categoria
          disabled
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
          stock(idSucursal: $idSucursal) {
            cantidad
          }
        }
      }
    `
  );

  const [saveMovimiento, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveMovimiento($input: MovimientoInput!) {
        saveMovimiento(input: $input) {
          id
        }
      }
    `
  );

  const handleSearch = (event) => {
    setIsWriting(true);
    setInputValue(event.target.value);
    setDropdownOpen(true);
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setIsWriting(false);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    if (origen) {
      getProductos({
        variables: {
          page: 1,
          search: search,
          filter: {
            tipo: ["Material", "Consumible", "Recurso"],
            id: {
              _notIn: productosSeleccionados.map((p) => p.id),
            },
            disabled: false,
          },
          idSucursal: origen.id,
        },
      });
    }
  }, [search, origen]);

  useEffect(() => {
    if (open && origen && !selectedMovimiento) {
      getSucursales({
        variables: {
          filter: {
            id: {
              _ne: origen.id,
            },
            administraStock: true,
          },
        },
      }).then((response) => {
        setSucursales(response.data.sucursales);
      });
      getProductos({
        variables: {
          page: 1,
          search: search,
          filter: {
            tipo: ["Material", "Consumible", "Recurso"],
            disabled: false,
            id: {
              _notIn: productosSeleccionados.map((p) => p.id),
            },
          },
          idSucursal: origen.id,
        },
      });
    } else if (open && selectedMovimiento) {
      setSucursales([]);
      setSearch("");
      setInputValue("");
      setDropdownOpen(false);
      setProductosSeleccionados(
        selectedMovimiento.movimientoDetalles
          ?.filter((detalle) => detalle.cantidad > 0)
          .map((detalle) => ({
            ...detalle.materiaPrima,
            cantidad: Math.abs(detalle.cantidad / detalle.materiaPrima.tipoUnidad?.multiplicador),
          }))
      );
    } else {
      setSucursales([]);
      setProductosSeleccionados([]);
      setSearch("");
      setInputValue("");
      setDropdownOpen(false);
    }
  }, [open, origen, search]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox display="flex" flexDirection="column" gap={2}>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedMovimiento ? "Ver Transferencia" : "Nueva Transferencia"}
                </SoftTypography>
              </SoftBox>
              <SoftBox>
                <Tooltip title="Cerrar" placement="top">
                  <Icon
                    fontSize="medium"
                    onClick={() => {
                      if (!loadingSave) {
                        handleClose();
                      }
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    close
                  </Icon>
                </Tooltip>
              </SoftBox>
            </SoftBox>
            <SoftBox display="flex" flexDirection="column" gap={2}>
              <SoftBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
                <SoftBox sx={{ width: "100%" }}>
                  <InputLabel>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Origen
                    </SoftTypography>
                  </InputLabel>
                  {loadingSucursales ? (
                    <Skeleton variant="text" width="100%" />
                  ) : (
                    <SoftInput
                      value={
                        selectedMovimiento
                          ? selectedMovimiento.sucursalOrigen?.nombre || ""
                          : origen?.nombre || ""
                      }
                      disabled
                    />
                  )}
                </SoftBox>
                <SoftBox>
                  <InputLabel>
                    <SoftTypography variant="caption" fontWeight="bold">
                      &nbsp;
                    </SoftTypography>
                  </InputLabel>
                  <Icon fontSize="large" sx={{ color: getUproColor("uproYellow") }}>
                    arrow_forward
                  </Icon>
                </SoftBox>
                <SoftBox sx={{ width: "100%" }}>
                  <InputLabel>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Destino
                    </SoftTypography>
                  </InputLabel>
                  {loadingSucursales ? (
                    <Skeleton variant="text" width="100%" />
                  ) : selectedMovimiento ? (
                    <SoftInput value={selectedMovimiento.sucursalDestino?.nombre || ""} disabled />
                  ) : (
                    <Select
                      fullWidth
                      value={destino || -1}
                      onChange={(e) => setDestino(e.target.value)}
                    >
                      <MenuItem value={-1} disabled>
                        Seleccione un destino
                      </MenuItem>
                      {sucursales.map((sucursal) => (
                        <MenuItem key={sucursal.id} value={sucursal.id}>
                          {sucursal.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </SoftBox>
              </SoftBox>
              <Collapse in={selectedMovimiento} mountOnEnter unmountOnExit>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MiniStatisticsCard
                      direction="left"
                      title={{ text: "Fecha de creación" }}
                      count={dayjs(selectedMovimiento?.createdAt).format("DD/MM/YYYY HH:mm")}
                      icon={{ color: getUproColor("uproBlue"), component: "access_time" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MiniStatisticsCard
                      direction="left"
                      title={{ text: "Transferido por" }}
                      count={makeNiceText(
                        `${selectedMovimiento?.user?.nombre} ${selectedMovimiento?.user?.apellido}`,
                        "nombre"
                      )}
                      icon={{ color: getUproColor("uproPink"), component: "person" }}
                    />
                  </Grid>
                </Grid>
              </Collapse>
              <Collapse in={destino || selectedMovimiento} mountOnEnter unmountOnExit>
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  <Collapse in={!selectedMovimiento} mountOnEnter unmountOnExit>
                    <SoftBox sx={{ position: "relative" }}>
                      <InputLabel>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Seleccionar un producto para agregar a la transferencia
                        </SoftTypography>
                      </InputLabel>
                      <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
                        <SoftBox>
                          <SoftBox
                            sx={{
                              position: "relative",
                              zIndex: 2,
                              backgroundColor: "#fff",
                              borderTopLeftRadius: 4,
                              borderTopRightRadius: 4,
                            }}
                          >
                            <SoftInput
                              placeholder="Escribe aquí el nombre o código del producto..."
                              icon={{ component: "search", direction: "left" }}
                              onChange={handleSearch}
                              value={inputValue}
                              icon2={
                                isWriting && {
                                  component: (
                                    <SoftBox
                                      display="flex"
                                      alignItems="center"
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      <CircularProgress size={15} color="primary" />
                                    </SoftBox>
                                  ),
                                  direction: "right",
                                }
                              }
                            />
                          </SoftBox>
                          <Collapse
                            in={dropdownOpen && Boolean(search) && data?.materiasPrimas?.length > 0}
                            mountOnEnter
                            unmountOnExit
                          >
                            <Card
                              sx={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                width: "100%",
                                boxSizing: "border-box",
                                backgroundColor: "#fff",
                                borderTopLeftRadius: "0px !important",
                                borderTopRightRadius: "0px !important",
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                paddingTop: "5px",
                                marginTop: "-5px",
                                borderTop: "0px",
                                maxHeight: 200,
                                overflowY: "auto",
                                zIndex: 1,
                              }}
                            >
                              <SoftBox display="flex" flexDirection="column" p={1}>
                                {loading ? (
                                  <SoftBox
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{
                                      height: 150,
                                      width: "100%",
                                    }}
                                    p={2}
                                  >
                                    <Skeleton variant="text" width="100%" />
                                    <Skeleton variant="text" width="100%" />
                                    <Skeleton variant="text" width="100%" />
                                    <Skeleton variant="text" width="100%" />
                                  </SoftBox>
                                ) : data?.materiasPrimas?.length > 0 ? (
                                  data?.materiasPrimas?.map((producto) => (
                                    <SoftBox
                                      display="flex"
                                      justifyContent="space-between"
                                      p={1}
                                      gap={2}
                                      key={producto?.id}
                                      sx={{
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: "#f0f0f0",
                                        },
                                      }}
                                      onClick={() => {
                                        setDropdownOpen(false);
                                        setInputValue("");
                                        setSearch("");
                                        setProductosSeleccionados((prev) => [
                                          ...prev,
                                          {
                                            ...producto,
                                            cantidad: 1,
                                          },
                                        ]);
                                      }}
                                    >
                                      <SoftBox display="flex" gap={1} alignItems="center">
                                        <Icon color="primary">school</Icon>
                                        <SoftTypography variant="h7" fontWeight="bold">
                                          {makeNiceText(`${producto?.nombre}`, "nombre")}
                                        </SoftTypography>
                                      </SoftBox>
                                      <SoftBadge
                                        color={producto?.stock?.cantidad ? "primary" : "secondary"}
                                        badgeContent={
                                          producto?.stock?.cantidad
                                            ? `${
                                                producto?.stock?.cantidad /
                                                  producto?.tipoUnidad?.multiplicador || 0
                                              } ${producto?.tipoUnidad?.abreviatura}`
                                            : "Sin stock"
                                        }
                                      />
                                    </SoftBox>
                                  ))
                                ) : (
                                  <SinDatos />
                                )}
                              </SoftBox>
                            </Card>
                          </Collapse>
                        </SoftBox>
                      </ClickAwayListener>
                    </SoftBox>
                  </Collapse>
                  <SoftBox>
                    <Card>
                      <SoftBox display="flex" flexDirection="column" p={2} gap={2}>
                        <SoftBox>
                          {loading ? (
                            <Loading />
                          ) : productosSeleccionados.length > 0 ? (
                            <Collapse
                              in={productosSeleccionados.length > 0}
                              mountOnEnter
                              unmountOnExit
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <SoftTypography variant="h6">
                                    {selectedMovimiento
                                      ? "Productos Transferidos"
                                      : "Productos Seleccionados"}
                                  </SoftTypography>
                                </Grid>
                                {productosSeleccionados.map((producto) => (
                                  <Grid item xs={12} key={producto?.id}>
                                    <Card>
                                      <SoftBox
                                        display="flex"
                                        justifyContent="space-between"
                                        gap={2}
                                        alignItems="center"
                                        p={2}
                                      >
                                        <SoftBox display="flex" flexDirection="column">
                                          <SoftTypography variant="h6" fontWeight="bold">
                                            {makeNiceText(`${producto?.nombre}`, "nombre")}
                                          </SoftTypography>
                                          <SoftBox display="flex" alignItems="center" gap={1}>
                                            {selectedMovimiento ? (
                                              <SoftTypography variant="caption">
                                                {producto?.categoria}
                                              </SoftTypography>
                                            ) : (
                                              <SoftTypography variant="caption">
                                                Disponible:{" "}
                                                {producto?.stock?.cantidad
                                                  ? `${
                                                      producto?.stock?.cantidad /
                                                      producto?.tipoUnidad?.multiplicador
                                                    } ${producto?.tipoUnidad?.abreviatura}`
                                                  : "Sin stock"}
                                              </SoftTypography>
                                            )}
                                          </SoftBox>
                                        </SoftBox>
                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                          <SoftBox sx={{ width: 150, maxWidth: "150px" }}>
                                            <Tooltip
                                              title={
                                                selectedMovimiento
                                                  ? "Cantidad transferida"
                                                  : producto?.cantidad >
                                                    producto?.stock?.cantidad /
                                                      producto?.tipoUnidad?.multiplicador
                                                  ? "Cantidad excede el stock disponible"
                                                  : producto?.cantidad < 1
                                                  ? "La cantidad debe ser al menos 1"
                                                  : "Cantidad a transferir"
                                              }
                                              placement="top"
                                            >
                                              {selectedMovimiento ? (
                                                <SoftInputNumber
                                                  value={producto?.cantidad}
                                                  disabled
                                                  icon={{
                                                    component: (
                                                      <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        sx={{
                                                          width: "100%",
                                                          height: "100%",
                                                        }}
                                                      >
                                                        <SoftTypography variant="h6">
                                                          {producto?.tipoUnidad?.abreviatura}
                                                        </SoftTypography>
                                                      </SoftBox>
                                                    ),
                                                    direction: "right",
                                                  }}
                                                />
                                              ) : (
                                                <SoftInputNumber
                                                  value={producto?.cantidad}
                                                  onChange={(event) => {
                                                    setProductosSeleccionados((prev) =>
                                                      prev.map((p) =>
                                                        p.id === producto?.id
                                                          ? { ...p, cantidad: event.target.value }
                                                          : p
                                                      )
                                                    );
                                                  }}
                                                  min={1}
                                                  max={
                                                    producto?.stock?.cantidad /
                                                    producto?.tipoUnidad?.multiplicador
                                                  }
                                                  error={
                                                    producto?.cantidad >
                                                      producto?.stock?.cantidad /
                                                        producto?.tipoUnidad?.multiplicador ||
                                                    producto?.cantidad < 1
                                                  }
                                                  icon={{
                                                    component: (
                                                      <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        sx={{
                                                          width: "100%",
                                                          height: "100%",
                                                        }}
                                                      >
                                                        <SoftTypography variant="h6">
                                                          {producto?.tipoUnidad?.abreviatura}
                                                        </SoftTypography>
                                                      </SoftBox>
                                                    ),
                                                    direction: "right",
                                                  }}
                                                />
                                              )}
                                            </Tooltip>
                                          </SoftBox>
                                          {!selectedMovimiento && (
                                            <Tooltip title="Eliminar producto" placement="top">
                                              <SoftButton
                                                color="primary"
                                                circular
                                                iconOnly
                                                onClick={() =>
                                                  setProductosSeleccionados((prev) =>
                                                    prev.filter((p) => p.id !== producto?.id)
                                                  )
                                                }
                                              >
                                                <Icon>close</Icon>
                                              </SoftButton>
                                            </Tooltip>
                                          )}
                                        </SoftBox>
                                      </SoftBox>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Collapse>
                          ) : (
                            <SinDatos />
                          )}
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  </SoftBox>
                </SoftBox>
              </Collapse>
              <Collapse in={!selectedMovimiento} mountOnEnter unmountOnExit>
                <SoftBox display="flex" justifyContent="flex-end">
                  <SoftButton
                    color="uproGreen"
                    circular
                    onClick={() => {
                      let detalles = productosSeleccionados.map((item) => ({
                        idMateriaPrima: item.id,
                        idSucursal: origen.id,
                        cantidad: -(item.cantidad * item.tipoUnidad?.multiplicador) || 0,
                        confirmado: true,
                      }));
                      detalles = detalles.concat(
                        productosSeleccionados.map((item) => ({
                          idMateriaPrima: item.id,
                          idSucursal: destino,
                          cantidad: item.cantidad * item.tipoUnidad?.multiplicador || 0,
                          confirmado: true,
                        }))
                      );
                      saveMovimiento({
                        variables: {
                          input: {
                            tipo: "Transferencia",
                            movimientoDetalle: detalles,
                            idSucursalOrigen: origen.id,
                            idSucursalDestino: destino,
                            observaciones: "",
                            idUser: user.id,
                          },
                        },
                      })
                        .then(() => {
                          handleSnackbar(
                            "La transferencia se ha realizado correctamente",
                            "success"
                          );
                          refetch();
                          handleClose();
                        })
                        .catch((e) => {
                          handleSnackbar(
                            e.message || "Ha ocurrido un error al realizar la transferencia",
                            "error"
                          );
                        });
                    }}
                    disabled={
                      !destino ||
                      loading ||
                      productosSeleccionados.length === 0 ||
                      productosSeleccionados.some(
                        (p) => p.cantidad < 1 || p.cantidad > p.stock?.cantidad
                      ) ||
                      loadingSave
                    }
                  >
                    {loadingSave ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <Icon>check</Icon>
                    )}
                    &nbsp; Transferir
                  </SoftButton>
                </SoftBox>
              </Collapse>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevaTransferencia.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  origen: PropTypes.object,
  refetch: PropTypes.func,
  selectedMovimiento: PropTypes.object,
};
